<!-- src/views/operation/OperationMain.vue -->
<template>
  <div class="operation-main-page">
    <!-- ===== 页面头部 ===== -->
    <div class="page-header">
      <div>
        <h1>Operation Main</h1>
      </div>
      <div class="header-actions">
        <el-badge :value="unreadCount" :hidden="unreadCount === 0">
          <el-button size="small" type="primary" plain @click="showNotifications = true">
            <el-icon><Bell /></el-icon> Notifications
          </el-button>
        </el-badge>
        <el-button size="small" type="success" plain @click="loadNotices">
          <el-icon><Refresh /></el-icon> Refresh
        </el-button>
      </div>
    </div>

    <!-- ===== 内容区域 ===== -->
    <div class="content-wrapper">
      <!-- 通知列表 -->
      <div class="notice-section">
        <div class="notice-header">
          <h3>
            <el-icon><Bell /></el-icon>
            Notifications
            <el-badge :value="unreadCount" class="notice-badge" v-if="unreadCount > 0" />
          </h3>
          <div class="notice-actions">
            <div class="search-container">
              <el-input
                v-model="searchKeyword"
                placeholder="Search title or content..."
                size="small"
                style="width: 200px;"
                clearable
                @keyup.enter="handleSearch"
                @clear="handleSearch"
                prefix-icon="Search"
              />
            </div>
            <el-button size="small" type="primary" plain @click="markAllAsRead">
              <el-icon><Check /></el-icon> Mark All as Read
            </el-button>
          </div>
        </div>
        
        <div class="table-wrapper">
          <el-table
            :data="filteredNoticeList"
            border
            style="width:100%"
            v-loading="loading"
            row-key="id"
            :row-class-name="getRowClassName"
            fit
            @row-dblclick="handleRowDoubleClick"
          >
            <el-table-column type="index" label="No" width="50" align="center" />
            
            <el-table-column prop="status" label="Status" width="80" align="center">
              <template #default="{ row }">
                <el-tag v-if="row.isRead" type="info" size="small">Read</el-tag>
                <el-tag v-else type="danger" size="small" effect="dark">Unread</el-tag>
              </template>
            </el-table-column>
            
            <el-table-column prop="title" label="Title" min-width="160" align="left">
              <template #default="{ row }">
                <span class="notice-title" :class="{ 'unread-title': !row.isRead }">
                  {{ row.title }}
                </span>
              </template>
            </el-table-column>
            
            <el-table-column prop="content" label="Content" min-width="200" align="left">
              <template #default="{ row }">
                <span class="notice-content">{{ row.content }}</span>
              </template>
            </el-table-column>
            
            <el-table-column prop="sender" label="Sender" width="100" align="center">
              <template #default="{ row }">
                <span class="sender-text">{{ row.sender }}</span>
              </template>
            </el-table-column>
            
            <el-table-column prop="sendTime" label="Send Time" width="160" align="center">
              <template #default="{ row }">
                <span class="time-text">{{ row.sendTime }}</span>
              </template>
            </el-table-column>
            
            <el-table-column prop="readTime" label="Read Time" width="160" align="center">
              <template #default="{ row }">
                <span class="time-text">{{ row.readTime || '-' }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        
        <!-- 空状态 -->
        <div v-if="!loading && filteredNoticeList.length === 0" class="empty-state">
          <el-empty :description="searchKeyword ? 'No matching notifications found' : 'No notifications available'" />
        </div>
      </div>
    </div>

    <!-- ===== 通知详情对话框 ===== -->
    <el-dialog
      v-model="dialogVisible"
      :title="selectedNotice?.title || '通知详情'"
      width="600px"
      :close-on-click-modal="true"
    >
      <div class="notice-detail" v-if="selectedNotice">
        <div class="detail-meta">
          <span class="meta-item">
            <strong>Sender:</strong>{{ selectedNotice.sender }}
          </span>
          <span class="meta-item">
            <strong>Send Time:</strong>{{ selectedNotice.sendTime }}
          </span>
          <span class="meta-item">
            <strong>Status:</strong>
            <el-tag v-if="selectedNotice.isRead" type="info" size="small">Read</el-tag>
            <el-tag v-else type="danger" size="small" effect="dark">Unread</el-tag>
          </span>
          <span class="meta-item" v-if="selectedNotice.readTime">
            <strong>Read Time:</strong>{{ selectedNotice.readTime }}
          </span>
        </div>
        <div class="detail-content">
          <strong>Content:</strong>
          <p>{{ selectedNotice.content }}</p>
        </div>
      </div>
      <template #footer>
        <el-button @click="dialogVisible = false">Close</el-button>
        <el-button v-if="selectedNotice && !selectedNotice.isRead" type="primary" @click="markAsRead(selectedNotice)">
          Mark as Read
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Bell, Refresh, Check } from '@element-plus/icons-vue'

// ============ 状态 ============
const loading = ref(false)
const noticeList = ref([])
const searchKeyword = ref('')
const dialogVisible = ref(false)
const selectedNotice = ref(null)
const showNotifications = ref(false)
let pollingTimer = null

// ============ 计算属性 ============
const unreadCount = computed(() => {
  return noticeList.value.filter(item => !item.isRead).length
})

const filteredNoticeList = computed(() => {
  if (!searchKeyword.value.trim()) {
    return noticeList.value
  }
  const keyword = searchKeyword.value.trim().toLowerCase()
  return noticeList.value.filter(item => 
    item.title.toLowerCase().includes(keyword) ||
    item.content.toLowerCase().includes(keyword)
  )
})

// ============ API 配置 ============
const getApiBase = () => {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'
}

const getHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ============ 数据映射 ============
const mapDbToFrontend = (dbData) => {
  return {
    id: dbData.id,
    title: dbData.title || '',
    content: dbData.content || '',
    sender: dbData.sender || '系统',
    sendTime: dbData.send_time ? formatDateTime(dbData.send_time) : '',
    isRead: dbData.is_read === 1 || dbData.is_read === true,
    readTime: dbData.read_time ? formatDateTime(dbData.read_time) : null
  }
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return ''
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  } catch (e) {
    return ''
  }
}

// ============ 获取通知列表 ============
const fetchNotices = async () => {
  try {
    const response = await fetch(`${getApiBase()}/api/auth/notices?role=operation`, {
      headers: getHeaders()
    })
    
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return []
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || '获取通知列表失败')
    }
    
    const result = await response.json()
    
    if (!result.success) {
      throw new Error(result.message || '获取通知列表失败')
    }
    
    return (result.data || []).map(mapDbToFrontend)
  } catch (error) {
    console.error('获取通知列表错误:', error)
    throw error
  }
}

const loadNotices = async () => {
  if (loading.value) return
  loading.value = true
  try {
    const data = await fetchNotices()
    noticeList.value = data
    console.log('📋 加载通知成功:', data.length, '条')
  } catch (error) {
    console.error('加载通知失败:', error)
    ElMessage.error(error.message || '加载通知列表失败')
  } finally {
    loading.value = false
  }
}

// ============ 搜索 ============
const handleSearch = () => {
  // 搜索逻辑已通过 computed 实现
}

// ============ 标记已读 ============
const markAsRead = async (row) => {
  try {
    const response = await fetch(`${getApiBase()}/api/auth/notices/${row.id}/read`, {
      method: 'PUT',
      headers: getHeaders()
    })
    
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || '标记已读失败')
    }
    
    // 更新本地数据
    row.isRead = true
    row.readTime = formatDateTime(new Date().toISOString())
    if (selectedNotice.value && selectedNotice.value.id === row.id) {
      selectedNotice.value.isRead = true
      selectedNotice.value.readTime = row.readTime
    }
    
    ElMessage.success('已标记为已读')
  } catch (error) {
    console.error('标记已读失败:', error)
    ElMessage.error(`操作失败: ${error.message}`)
  }
}

const markAllAsRead = async () => {
  const unreadItems = noticeList.value.filter(item => !item.isRead)
  if (unreadItems.length === 0) {
    ElMessage.info('没有未读通知')
    return
  }
  
  try {
    const ids = unreadItems.map(item => item.id)
    const response = await fetch(`${getApiBase()}/api/auth/notices/read-all`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ ids, role: 'operation' })
    })
    
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || '批量标记失败')
    }
    
    const now = formatDateTime(new Date().toISOString())
    unreadItems.forEach(item => {
      item.isRead = true
      item.readTime = now
    })
    ElMessage.success(`已标记 ${unreadItems.length} 条通知为已读`)
  } catch (error) {
    console.error('批量标记已读失败:', error)
    ElMessage.error(`操作失败: ${error.message}`)
  }
}

// ============ 查看详情 ============
const viewNotice = (row) => {
  selectedNotice.value = row
  dialogVisible.value = true
  if (!row.isRead) {
    markAsRead(row)
  }
}

// ============ 双击行查看详情 ============
const handleRowDoubleClick = (row) => {
  viewNotice(row)
}

// ============ 行样式 ============
const getRowClassName = ({ row }) => {
  return row.isRead ? 'read-row' : 'unread-row'
}

// ============ 轮询获取新通知 ============
const startPolling = () => {
  pollingTimer = setInterval(async () => {
    try {
      const data = await fetchNotices()
      if (data.length > 0) {
        const oldMap = new Map(noticeList.value.map(item => [item.id, item]))
        let hasNew = false
        
        data.forEach(newItem => {
          const oldItem = oldMap.get(newItem.id)
          if (!oldItem) {
            hasNew = true
          } else if (oldItem.isRead !== newItem.isRead) {
            oldItem.isRead = newItem.isRead
            oldItem.readTime = newItem.readTime
          }
        })
        
        if (hasNew) {
          noticeList.value = data
          ElMessage.info('收到新通知')
        }
      }
    } catch (error) {
      console.debug('轮询通知失败:', error)
    }
  }, 30000)
}

// ============ 生命周期 ============
onMounted(() => {
  loadNotices()
  startPolling()
})

onBeforeUnmount(() => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
})
</script>

<style scoped>
.operation-main-page {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

/* ===== 页面头部 ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 12px;
}

.page-header h1 {
  font-size: 24px;
  color: #1a2332;
  font-weight: 700;
  margin: 0;
}

.page-header p {
  color: #6c7a8a;
  margin: 4px 0 0 0;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* ===== 内容区域 ===== */
.content-wrapper {
  flex: 1;
  display: flex;
  gap: 16px;
  min-height: 0;
}

/* ===== 主内容 ===== */
.main-content {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* ===== Notice 区域 ===== */
.notice-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 16px 20px;
}

.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #ebeef5;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
}

.notice-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notice-badge {
  margin-left: 4px;
}

.notice-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.search-container {
  display: flex;
  align-items: center;
}

.search-container .el-input {
  width: 200px;
}

.search-container .el-input :deep(.el-input__wrapper) {
  height: 30px;
  min-height: 30px;
}

.search-container .el-input :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  font-size: 12px;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  padding-top: 12px;
  min-height: 0;
}

/* ============ 表格样式 ============ */
:deep(.el-table) {
  font-size: 13px;
  width: 100% !important;
}

:deep(.el-table .cell) {
  padding: 2px 4px;
}

:deep(.el-table td.el-table__cell) {
  padding: 2px 0 !important;
}

:deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 11px !important;
  padding: 4px 0 !important;
}

:deep(.el-table th.el-table__cell .cell) {
  font-size: 11px !important;
  line-height: 20px !important;
  color: #ffffff !important;
}

/* 行样式 - 双击查看详情 */
:deep(.el-table__row) {
  cursor: pointer;
}

:deep(.unread-row) {
  background-color: #fef6f6 !important;
  font-weight: 500;
}

:deep(.unread-row:hover) {
  background-color: #fde8e8 !important;
}

:deep(.read-row) {
  background-color: #ffffff !important;
}

:deep(.read-row:hover) {
  background-color: #f5f7fa !important;
}

.notice-title {
  font-size: 13px;
  color: #303133;
}

.unread-title {
  font-weight: 600;
  color: #1a2332;
}

.notice-content {
  font-size: 12px;
  color: #606266;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sender-text {
  font-size: 12px;
  color: #606266;
}

.time-text {
  font-size: 12px;
  color: #909399;
}

/* ============ 空状态 ============ */
.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

/* ============ 详情对话框 ============ */
.notice-detail {
  padding: 4px 0;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  margin-bottom: 16px;
}

.meta-item {
  font-size: 13px;
  color: #606266;
}

.meta-item strong {
  color: #303133;
}

.detail-content {
  padding: 4px 0;
}

.detail-content strong {
  display: block;
  font-size: 14px;
  color: #303133;
  margin-bottom: 8px;
}

.detail-content p {
  font-size: 14px;
  color: #606266;
  line-height: 1.8;
  padding: 8px 16px;
  background: #fafafa;
  border-radius: 6px;
  border-left: 3px solid #3498db;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ============ 滚动条 ============ */
.table-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}

/* ============ 对话框样式 ============ */
:deep(.el-dialog) {
  border-radius: 12px;
}

:deep(.el-dialog__header) {
  border-bottom: 1px solid #ebeef5;
  padding: 16px 20px;
}

:deep(.el-dialog__body) {
  padding: 20px;
}

:deep(.el-dialog__footer) {
  border-top: 1px solid #ebeef5;
  padding: 12px 20px;
}
</style>